import React, { useState } from "react";
// 1. Import Helmet
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

const SUBJECT_OPTIONS = [
  "General Inquiry",
  "General Information",
  "Pricing",
  "Other",
];

// Define metadata outside the component to prevent re-creation on every render
const PAGE_TITLE = "Contact Steminorder | Get in Touch with Our Shipping Experts";
const PAGE_DESCRIPTION = "Have questions about our shipping platform, pricing, or services? Contact the Steminorder team directly via our form or phone number for prompt assistance.";
const CANONICAL_URL = "https://www.steminorder.com/contact-us";
const OG_IMAGE_URL = "https://www.steminorder.com/images/logo-share.png"; // Use a general logo for contact page sharing

const ContactPage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phonenum, setPhonenum] = useState("");

  const [message, setMessage] = useState("");
  const [contactusmailtype, setContactusmailtype] = useState("General Inquiry");

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  async function sendContactUs(payload) {
    const endpoint = `${process.env.REACT_APP_API_ENDPOINT}/mail/contactus/entry`;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to send contact form");
    }
    return response.json();
  }

  function validate() {
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!email.trim()) return "Email is required.";

    if (!message.trim()) return "Message is required.";
    // Optional: phonenum format check
    return "";
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setOkMsg("");

    const v = validate();
    if (v) {
      setErrorMsg(v);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        subject: "New Contact Us Message",
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: email.trim(),
        message: message.trim(),
        phonenum: phonenum.trim() || null, // your DB column is phonenum
        contactusmailtype, // one of enum('General Inquiry','General Information','Pricing','Other')
      };

      await sendContactUs(payload);
      setOkMsg("Your message has been sent. We’ll get back to you shortly.");
      // Optionally clear the form:
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhonenum("");

      setMessage("");
      setContactusmailtype("General Inquiry");
    } catch (err) {
      setErrorMsg(err.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ============================================================
        REACT HELMET INTEGRATION 
        ============================================================
      */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Steminorder" />
        <meta property="og:image" content={OG_IMAGE_URL} /> 
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>
      
      <Header />

      <div className="container">
        <div className="row my-5">
          <div className="col-lg-4 col-sm-12 bg-header-green text-white pt-10 pb-56">
            <h1 className="pb-5 px-5 text-3xl">Contact Us</h1>
            <p className="px-5 text-xl text-gray-300">
              Whether you have inquiries, feedback, or need assistance, our
              dedicated team is just a message away. Feel free to reach out via
              the contact form, and we’ll get back to you promptly. Your
              satisfaction is our priority!
            </p>
          </div>

          <div className="col-lg-8 col-sm-12 py-4">
            <form onSubmit={onSubmit} noValidate>
              {/* Alerts */}
              {errorMsg && (
                <div className="alert alert-danger mx-4 mt-4" role="alert">
                  {errorMsg}
                </div>
              )}
              {okMsg && (
                <div className="alert alert-success mx-4 mt-4" role="alert">
                  {okMsg}
                </div>
              )}

              {/* Name row */}
              <div className="row mb-5">
                <div className="col-lg-5 offset-lg-1 col-sm-12 pr-3">
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control border-bottom shadow-none"
                      id="firstNameInput"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <label
                      className="font-semibold text-sm"
                      htmlFor="firstNameInput"
                    >
                      First Name
                    </label>
                  </div>
                </div>

                <div className="col-lg-5 col-sm-12 pl-3-lg">
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control border-bottom shadow-none"
                      id="lastNameInput"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    <label
                      className="font-semibold text-sm"
                      htmlFor="lastNameInput"
                    >
                      Last Name
                    </label>
                  </div>
                </div>
              </div>

              {/* Email + Phone */}
              <div className="row mb-5">
                <div className="col-lg-5 offset-lg-1 col-sm-12 pr-3">
                  <div className="form-floating mb-3">
                    <input
                      type="email"
                      className="form-control border-bottom shadow-none"
                      id="emailInput"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <label
                      className="font-semibold text-sm"
                      htmlFor="emailInput"
                    >
                      E-mail
                    </label>
                  </div>
                </div>

                <div className="col-lg-5 col-sm-12 pl-3-lg">
                  <div className="form-floating mb-3">
                    <input
                      type="tel"
                      className="form-control border-bottom shadow-none"
                      id="phoneInput"
                      placeholder="Phone Number (optional)"
                      value={phonenum}
                      onChange={(e) => setPhonenum(e.target.value)}
                    />
                    <label
                      className="font-semibold text-sm"
                      htmlFor="phoneInput"
                    >
                      Phone Number (optional)
                    </label>
                  </div>
                </div>
              </div>

              {/* Subject (enum) */}
              <div className="row mb-5">
                <div className="col-lg-11 offset-lg-1">
                  <h5 className="mb-4 font-semibold text-gray-900">
                    Select Subject
                  </h5>
                  <ul className="items-center w-full text-sm font-medium text-gray-900 bg-white rounded-lg sm:flex dark:bg-gray-700 dark:text-white p-0">
                    {SUBJECT_OPTIONS.map((opt) => {
                      const id = `list-radio-${opt
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`;
                      return (
                        <li className="w-full" key={opt}>
                          <div className="flex items-center ps-3">
                            <input
                              id={id}
                              type="radio"
                              value={opt}
                              name="contactusmailtype"
                              checked={contactusmailtype === opt}
                              onChange={(e) =>
                                setContactusmailtype(e.target.value)
                              }
                              className="w-4 h-4 text-header-green bg-gray-100 border-gray-300 focus:ring-2"
                            />
                            <label
                              htmlFor={id}
                              className="w-full py-3 ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                            >
                              {opt}
                            </label>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Message */}
              <div className="row">
                <div className="col-11 offset-1">
                  <h5 className="mb-4 font-semibold text-gray-900">Message</h5>
                  <div className="form-floating">
                    <textarea
                      className="form-control border-bottom shadow-none"
                      placeholder="Write your message..."
                      id="messageTextarea"
                      style={{ minHeight: 160 }}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    <label htmlFor="messageTextarea">
                      Write your message...
                    </label>
                  </div>
                </div>

                <div className="row pt-4">
                  <div className="d-flex justify-end">
                    <button
                      type="submit"
                      className="btn btn-primary px-5 py-3 rounded-2 font-bold"
                      disabled={submitting}
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        /* Make inputs display black text (placeholders included across browsers) */
        .form-control { color: #000 !important; }
        ::placeholder { color: #000; opacity: 1; }
        :-ms-input-placeholder { color: #000; }
        ::-ms-input-placeholder { color: #000; }
      `}</style>

      <Footer />
    </>
  );
};

export default ContactPage;